import { useSize } from 'ahooks';
import { Tooltip } from 'antd';
import { TextAreaProps } from 'antd/lib/input';
import React, { FC, useRef, useState } from 'react';
import styled from 'styled-components';
import { SingleLineEllipsis } from 'components/styles';
import { HeightStyle, InputBackgroundColor } from './input';
import { TextArea } from './text-area';

const Wrapper = styled.div<{
  wrapperWidth: number;
  wrapperTop: number;
  contentWidth: number;
  contentMaxWidth?: string;
}>`
  position: relative;
  height: ${HeightStyle};
  background-color: ${InputBackgroundColor};
  max-width: 100%;
  overflow: hidden;

  span {
    position: absolute;
    visibility: hidden;
    display: inline-block;
    padding: calc(var(--spacing-04) - 1.5px) var(--spacing-05);
    max-width: ${({ contentMaxWidth }) => contentMaxWidth ?? '100%'};
    height: ${HeightStyle};
    ${SingleLineEllipsis};
  }

  ${TextArea} {
    padding: calc(var(--spacing-04) - 1.5px) var(--spacing-05);
    max-height: ${HeightStyle} !important;
    ${SingleLineEllipsis};
    transition: all 0.3s, height 0s, width 0s, max-width 0s, min-width 0s,
      top 0s;
  }

  &:focus-within {
    ${TextArea} {
      max-height: unset !important;
      overflow: auto;
      white-space: normal;
      width: ${({ contentWidth }) => `${contentWidth}px`};
      min-width: ${({ wrapperWidth }) => `${wrapperWidth}px`};
      max-width: ${({ contentMaxWidth, wrapperWidth }) =>
        contentMaxWidth ?? `${wrapperWidth}px`};
      position: fixed;
      top: ${({ wrapperTop }) => `${wrapperTop}px`};
      z-index: var(--zindex-layer-00);
    }
  }
`;

const getLongestLine = (value: string) =>
  value
    .split('\n')
    .reduce(
      (longLine, currentLine) =>
        currentLine.length > longLine.length ? currentLine : longLine,
      '',
    );

export const ExpandableInput: FC<
  TextAreaProps & {
    contentMaxWidth?: string;
  }
> = ({
  value,
  autoSize,
  placeholder,
  onFocus,
  onBlur,
  contentMaxWidth,
  ...rest
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { width: wrapperWidth = 0 } = useSize(wrapperRef);
  const [wrapperTop, setWrapperTop] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const { width: contentWidth = 0 } = useSize(contentRef);
  const [focussed, setFocussed] = useState(false);

  const showPlaceholder = value == null || value.toString().trim().length === 0;

  return (
    <Tooltip title={rest.disabled ? value : ''}>
      <Wrapper
        ref={wrapperRef}
        wrapperWidth={wrapperWidth}
        wrapperTop={wrapperTop}
        contentWidth={contentWidth + 32}
        contentMaxWidth={contentMaxWidth}
      >
        <span ref={contentRef}>
          {showPlaceholder
            ? placeholder
            : getLongestLine(value?.toString() ?? '')}
        </span>
        <TextArea
          {...rest}
          value={focussed ? value : value?.toString().replace(/\n/g, ' ')}
          onFocus={(e) => {
            setWrapperTop(wrapperRef.current?.getBoundingClientRect().top ?? 0);
            setFocussed(true);
            onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocussed(false);
            onBlur?.(e);
          }}
          autoSize={autoSize ?? { maxRows: 6 }}
          placeholder={placeholder}
        />
      </Wrapper>
    </Tooltip>
  );
};
